<template>
  <div class="min-h-screen">
    <!-- Booking Search Section (copied from Home.vue) -->
    <div
      class="w-full max-w-5xl bg-white/90 rounded-2xl shadow-lg p-6 mb-10 mt-4 mx-auto from-blue-50"
    >
      <!-- Search Form: All in one line -->
      <form class="flex flex-col gap-3 md:flex-row md:gap-3 items-center w-full" @submit.prevent="onSearch">
      
        <div class="flex-1 w-full min-w-[200px]">
          <div class="relative">
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
            >
              <i class="bi bi-geo-alt"></i>
            </span>
            <input
              v-model="search.city"
              class="pl-10 pr-6 border border-gray-200 rounded-lg h-12 w-full focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-300 transition shadow-sm"
              placeholder="Enter city or location"
              required
              @input="searchCities"
              @focus="searchCities"
              @blur="handleCityBlur"
              autocomplete="off"
            />
            <div
              v-if="showCityDropdown && cities.length"
              class="absolute z-10 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-auto"
            >
              <div v-if="cityLoading" class="p-2 text-gray-500 text-center">
                Loading...
              </div>
              <div v-else>
                <div
                  v-for="city in cities"
                  :key="city.id"
                  @mousedown.prevent="selectCity(city)"
                  class="px-4 py-2 hover:bg-blue-100 cursor-pointer"
                >
                  {{ city.name }}
                  <span v-if="city.state">, {{ city.state.name }}</span>
                  <span v-if="city.state && city.state.country"
                    >, {{ city.state.country.name }}</span
                  >
                </div>
              </div>
            </div>
            <span
              class="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300 pointer-events-none"
            >
              <i class="bi bi-chevron-down"></i>
            </span>
          </div>
        </div>
        <!-- Date Picker -->
        <div class="flex-1 w-full min-w-[250px] md:w-auto">
          <div class="relative" data-input>
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
            >
              <i class="bi bi-calendar-date"></i>
            </span>
            <flat-pickr
              v-model="search.dateRange"
              :config="dateOptions"
              class="pl-12 pr-6 w-full border border-gray-200 rounded-lg h-12 px-4 focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-300 transition shadow-sm"
              placeholder="Select check-in and check-out"
              :readonly="true"
            >
              <input
                type="text"
                data-input
                readonly
                class="pl-12 pr-6 w-full border border-gray-200 rounded-lg h-12 px-4 focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-300 transition shadow-sm"
                placeholder="Select check-in and check-out"
              />
            </flat-pickr>
          </div>
        </div>
        <!-- Adults -->
        <div class="flex-shrink-0 w-full md:w-auto min-w-[120px]">
          <div class="relative">
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
            >
              <i class="bi bi-people"></i>
            </span>
            <select
              v-model="search.adults"
              class="pl-10 pr-6 border border-gray-200 rounded-lg h-12 w-full md:w-[120px] focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-400 transition shadow-sm appearance-none"
              required
            >
              <option disabled value="">Adult</option>
              <option v-for="n in 5" :key="n" :value="n">
                {{ n }} Adult{{ n > 1 ? "s" : "" }}
              </option>
            </select>
            <span
              class="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300 pointer-events-none"
            >
              <i class="bi bi-chevron-down"></i>
            </span>
          </div>
        </div>
        <!-- Children -->
        <div class="flex-shrink-0 w-full md:w-auto min-w-[120px]">
          <div class="relative">
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400 pointer-events-none"
            >
              <i class="bi bi-person-badge"></i>
            </span>
            <select
              v-model="search.children"
              class="pl-10 pr-6 border border-gray-200 rounded-lg h-12 w-full md:w-[120px] focus:outline-none focus:ring-2 focus:ring-blue-200 text-base bg-white hover:border-blue-400 transition shadow-sm appearance-none"
            >
              <option disabled value="">Child</option>
              <option v-for="n in 5" :key="n" :value="n">
                {{ n }} Child{{ n > 1 ? "ren" : "" }}
              </option>
            </select>
            <span
              class="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300 pointer-events-none"
            >
              <i class="bi bi-chevron-down"></i>
            </span>
          </div>
        </div>
        <!-- Search Button -->
        <div class="flex-shrink-0 w-full md:w-auto">
          <button
            type="submit"
            class="h-12 w-full md:w-auto bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-lg px-6 font-bold shadow-lg transition flex items-center justify-center"
          >
            <i class="bi bi-search mr-2"></i>Search
          </button>
        </div>
      </form>
    </div>
    <!-- End Booking Search Section -->

    <!-- Payment Cancellation Message -->
    <div v-if="$route.query.payment_canceled" class="max-w-5xl mx-auto px-8 py-4">
      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <i class="bi bi-exclamation-triangle text-yellow-400 text-xl"></i>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-yellow-800">Payment Cancelled</h3>
            <div class="mt-2 text-sm text-yellow-700">
              <p>
                Your payment was cancelled. You can try booking again or contact support
                if you need assistance.
              </p>
            </div>
            <div class="mt-3">
              <button
                @click="$router.push('/hotels')"
                class="text-sm text-yellow-800 underline hover:text-yellow-900"
              >
                Dismiss this message
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto flex flex-col md:flex-row px-4 md:px-8 py-6 md:py-8">
      <!-- Sidebar Filters -->
      <aside
        class="w-full md:w-80 p-4 md:p-6 bg-white border-b md:border-r md:border-b-0 flex-shrink-0"
      >
        <div class="flex items-center justify-between md:block">
          <h3 class="font-bold text-lg mb-2 md:mb-4">Filter by:</h3>
          <!-- Mobile toggle -->
          <button
            class="md:hidden text-blue-600 text-sm underline"
            @click="showFilters = !showFilters"
          >
            {{ showFilters ? "Hide" : "Show" }} Filters
          </button>
        </div>

        <!-- Filters content -->
        <div v-show="showFilters" class="mt-3 md:mt-0">
          <div class="font-semibold mb-2">Facilities</div>
          <div class="flex flex-col gap-2 max-h-60 overflow-auto">
            <label
              v-for="facility in facilities"
              :key="facility.id"
              class="flex items-center"
            >
              <input
                type="checkbox"
                class="mr-2"
                :value="facility.id"
                v-model="selectedFacilities"
                @change="onFacilityChange"
              />
              {{ facility.name }}
            </label>
          </div>
        </div>
      </aside>

      <!-- Main Content -->
      <main class="flex-1 md:pl-8 mt-4 md:mt-0">
        <div class="max-w-full">
          <!-- Hotel Cards -->
          <div
            v-for="hotel in hotels"
            :key="hotel.id"
            class="flex flex-col md:flex-row bg-white rounded-lg shadow p-4 mb-6 border hover:shadow-lg transition"
          >
            <!-- Hotel Image -->
            <img
              :src="
                hotel.galleries && hotel.galleries.length > 0
                  ? hotel.galleries[0].path
                  : hotel1
              "
              :alt="hotel.name"
              class="w-full md:w-48 h-48 md:h-auto object-cover rounded mb-4 md:mb-0 md:mr-6 cursor-pointer"
              @click="goToHotelDetails(hotel)"
              @error="handleImageError"
            />

            <!-- Hotel Details -->
            <div class="flex-1 flex flex-col justify-between">
              <div>
                <h4
                  class="font-semibold text-lg md:text-xl text-blue-800 hover:underline cursor-pointer"
                  @click="goToHotelDetails(hotel)"
                >
                  {{ hotel.name }}
                </h4>
                <div class="text-blue-700 text-sm mb-1">
                  <span v-if="hotel.address">{{ hotel.address }}</span>
                  <span v-if="hotel.city">, {{ hotel.city.name }}</span>
                  <span v-if="hotel.state">, {{ hotel.state.name }}</span>
                </div>
                <ul
                  v-if="hotel.amenities && hotel.amenities.length"
                  class="text-green-700 text-xs my-2 list-none p-0"
                >
                  <li v-for="amenity in hotel.amenities.slice(0, 2)" :key="amenity.id">
                    ✔ {{ amenity.facility.name }}
                  </li>
                </ul>
                <div
                  v-if="hotel.starting_price"
                  class="mt-2 md:mt-3 mb-2 text-base md:text-lg font-bold"
                >
                  <span class="text-gray-700">Starting from </span>
                  <span class="text-blue-600 text-xl md:text-2xl font-extrabold"
                    >$ {{ hotel.starting_price }}</span
                  >
                </div>
              </div>
              <div class="flex items-center justify-end mt-2">
                <button
                  class="bg-blue-600 text-white px-3 md:px-4 py-2 rounded hover:bg-blue-700 text-sm md:text-base"
                  @click="goToHotelDetails(hotel)"
                >
                  See availability
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import hotel1 from "@/assets/hotel1.jpg";
import { request } from "@/Util/Request";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
export default {
  name: "UserHotels",
  components: { FlatPickr },
  data() {
    return {
      hotels: [],
      loading: false,
      showFilters: false,
      hotel1,
      search: {
        city: "",
        adults: "",
        children: "",
        dateRange: [],
      },
      totalHotels: 0,
      showCityDropdown: false,
      cityLoading: false,
      cities: [],
      selectedCity: null,
      cityId: null,
      facilities: [], // <-- Add for dynamic facilities
      selectedFacilities: [], // <-- Add for selected facility IDs
      dateOptions: {
        mode: "range",
        dateFormat: "d M Y",
        altInput: true,
        altFormat: "d M Y",
        allowInput: true,
        showMonths: 2,
        minDate: "today",
        wrap: true,
        onReady(selectedDates, dateStr, instance) {
          if (instance.altInput) {
            instance.altInput.setAttribute("readonly", "readonly");
          }
        },
        onOpen(selectedDates, dateStr, instance) {
          if (instance.altInput) {
            instance.altInput.setAttribute("readonly", "readonly");
          }
        },
      },
      viewMode: "list",
      showInfoAlert: true,
    };
  },
  async created() {
    this.syncStateFromRoute(this.$route.query);
    await Promise.all([this.fetchFacilities(), this.fetchHotelsOrFiltered()]);
    this.loading = false;
  },
  methods: {
    handleImageError(event) {
      event.target.src = this.hotel1;
    },

    syncStateFromRoute(query) {
      this.cityId = query.cityId || null;
      this.search.city = query.city || "";
      this.search.adults = query.adults || "";
      this.search.children = query.children || "";
      if (query.check_in && query.check_out) {
        this.search.dateRange = [query.check_in, query.check_out];
      } else if (query.checkin && query.checkout) {
        this.search.dateRange = [query.checkin, query.checkout];
      } else {
        this.search.dateRange = [];
      }

      // Set selectedCity if we have cityId and city name
      if (this.cityId && this.search.city) {
        this.selectedCity = { id: this.cityId, name: this.search.city };
      } else {
        // Clear selectedCity if no city is selected
        this.selectedCity = null;
      }
    },

    async onSearch() {
      let checkin = "";
      let checkout = "";

      if (
        typeof this.search.dateRange === "string" &&
        this.search.dateRange.includes(" to ")
      ) {
        [checkin, checkout] = this.search.dateRange.split(" to ");
      } else if (
        Array.isArray(this.search.dateRange) &&
        this.search.dateRange.length === 2
      ) {
        [checkin, checkout] = this.search.dateRange;
      }

      if (!checkin || !checkout) {
        alert("Please select both check-in and check-out dates.");
        return;
      }

      // Reuse previous selection if no new one is made
      if (!this.selectedCity && this.cityId && this.search.city) {
        this.selectedCity = { id: this.cityId, name: this.search.city };
      }

      let cityId = this.selectedCity?.id;
      const cityName = this.selectedCity?.name || this.search.city;

      const newQuery = {
        city: cityName,
        checkin,
        checkout,
        adults: this.search.adults,
        children: this.search.children,
      };

      if (cityId) {
        newQuery.cityId = cityId;
      }

      const currentQuery = this.$route.query;
      const newUrl = this.$router.resolve({ path: this.$route.path, query: newQuery })
        .href;
      const currentUrl = this.$router.resolve({
        path: this.$route.path,
        query: currentQuery,
      }).href;

      this.cityId = cityId;

      if (newUrl !== currentUrl) {
        this.$router.replace({ path: this.$route.path, query: newQuery });
      } else {
        await this.fetchHotelsOrFiltered();
      }
    },
    async fetchHotelsOrFiltered() {
      this.loading = true;
      try {
        // 💡 Normalize dateRange if it’s a string
        if (
          typeof this.search.dateRange === "string" &&
          this.search.dateRange.includes(" to ")
        ) {
          this.search.dateRange = this.search.dateRange.split(" to ");
        }

        let hotels;

        if (
          this.cityId &&
          Array.isArray(this.search.dateRange) &&
          this.search.dateRange.length === 2 &&
          this.search.adults
        ) {
          const [checkin, checkout] = this.search.dateRange;
          const response = await request({
            method: "get",
            url: "/public/available-hotels",
            params: {
              city_id: this.cityId,
              check_in: checkin,
              check_out: checkout,
              adults: this.search.adults,
              children: this.search.children,
            },
          });
          hotels = response.data.hotels || response.data.data || response.data;
        } else {
          const response = await request({
            method: "get",
            url: "/public/hotels",
          });
          hotels = response.data.hotels || response.data.data || response.data;
        }
        if (!Array.isArray(hotels)) {
          hotels = [];
        }
        this.totalHotels = hotels.length; // <-- Set total before filtering
        // Apply frontend facility filter if any selected
        if (this.selectedFacilities.length > 0) {
          hotels = hotels.filter((hotel) => {
            if (!hotel.amenities || hotel.amenities.length === 0) return false;
            const hotelFacilityIds = hotel.amenities
              .map((a) => a.facility?.id)
              .filter(Boolean);
            return this.selectedFacilities.every((fid) => hotelFacilityIds.includes(fid));
          });
        }
        this.hotels = hotels;
      } catch (error) {
        console.error("Error fetching hotels:", error);
        this.hotels = { error: error.message };
      } finally {
        this.loading = false;
      }
    },
    async fetchFacilities() {
      try {
        const response = await request({
          method: "get",
          url: "/public/hotel-facilities",
        });
        const hotelFacilities = response.data || [];
        // Flatten all facilities arrays and deduplicate by id
        const allFacilities = hotelFacilities.flatMap((hf) => hf.facilities || []);
        const uniqueFacilitiesMap = {};
        allFacilities.forEach((facility) => {
          if (facility && facility.id && !uniqueFacilitiesMap[facility.id]) {
            uniqueFacilitiesMap[facility.id] = facility;
          }
        });
        this.facilities = Object.values(uniqueFacilitiesMap);
      } catch (error) {
        this.facilities = [];
      }
    },
    goToHotelDetails(hotel) {
      let checkin = "";
      let checkout = "";

      // Use date from search or default to today + tomorrow
      if (
        typeof this.search.dateRange === "string" &&
        this.search.dateRange.includes(" to ")
      ) {
        [checkin, checkout] = this.search.dateRange.split(" to ");
      } else if (
        Array.isArray(this.search.dateRange) &&
        this.search.dateRange.length === 2
      ) {
        [checkin, checkout] = this.search.dateRange;
      } else {
        // Default dates: today and tomorrow
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        const formatDate = (d) =>
          d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });

        checkin = formatDate(today);
        checkout = formatDate(tomorrow);
      }

      // Get city information from hotel or search
      const hotelCity = hotel.city?.name || hotel.city || "";
      const hotelCityId = hotel.city_id || hotel.city?.id || "";

      // Default values if not in search
      const query = {
        city: this.search.city || hotelCity || "",
        cityId: this.cityId || hotelCityId || "",
        check_in: checkin,
        check_out: checkout,
        adults: this.search.adults || 2,
        children: this.search.children || 0,
      };

      this.$router.push({
        path: `/hotels/${hotel.id}`,
        query,
      });
    },

    onFacilityChange() {
      // Re-filter hotels when facilities change
      this.fetchHotelsOrFiltered();
    },
    async searchCities() {
      const query = this.search.city;
      if (query.length >= 2) {
        this.cityLoading = true;
        try {
          const response = await request({
            method: "get",
            url: `/cities?search=${encodeURIComponent(query)}`,
          });
          if (response.data) {
            this.cities = response.data;
            this.showCityDropdown = true;
          } else {
            this.cities = [];
            this.showCityDropdown = false;
          }
        } catch (error) {
          this.cities = [];
          this.showCityDropdown = false;
        } finally {
          this.cityLoading = false;
        }
      } else {
        this.cities = [];
        this.showCityDropdown = false;
      }
    },
    handleCityBlur() {
      setTimeout(() => {
        this.showCityDropdown = false;
      }, 200);
    },
    selectCity(cityObj) {
      this.search.city = cityObj.name;
      this.selectedCity = cityObj;
      this.showCityDropdown = false;
      this.cities = [];
      this.cityId = cityObj.id;
    },
    clearCity() {
      console.log("Clearing city filter");
      this.search.city = "";
      this.cityId = null;
      this.selectedCity = null;

      // Update URL to remove city filter
      const currentQuery = { ...this.$route.query };
      delete currentQuery.city;
      delete currentQuery.cityId;

      console.log("Updated query:", currentQuery);

      this.$router.replace({
        path: this.$route.path,
        query: currentQuery,
      });

      // Re-fetch hotels without city filter
      this.fetchHotelsOrFiltered();
    },
    removeFacility(facilityId) {
      this.selectedFacilities = this.selectedFacilities.filter((id) => id !== facilityId);
      // Re-filter hotels when facility is removed
      this.fetchHotelsOrFiltered();
    },
  },
  watch: {
    "$route.query": {
      handler(newQuery) {
        this.syncStateFromRoute(newQuery);
        this.fetchHotelsOrFiltered();
      },
      immediate: false,
    },
  },
};
</script>
